import { SortMode } from "@lxc/app-device-common";
import { APP_NAME } from "~/core/constants/constants";
import { Filters } from "~/core/models/filters";
import { DtwinConfigurationType } from "~/modules/dtwin/models/DtwinConfigurationType.enum";
import type { DynamicUI } from "~/modules/dtwin/models/DynamicUI.interface";
import { DynamicUIDescriptionColumn } from "~/modules/dtwin/models/DynamicUIDescriptionColumn.enum";
import { FirmwareUpdateSourceOption } from "~/modules/dtwin/models/FirmwareUpdateSourceOption.enum";

/**
 * Notes:
 *  - Update values of attribute "id" can break values of attributes
 *    "afterElementId" and "beforeElementId" in the specific device model UI configs (https://gitlab.tools.lx-connect.com/lxconnect/lx-iot-backend/lxc-bs-group/dtwin-models)
 */
export const DEFAULT_DTWIN_MODEL_UI_CONFIG: DynamicUI = {
  list: {
    columns: [
      {
        id: "attributes.friendlyName",
        label: "dtwins.list.attributes.friendlyName",
        sortMode: SortMode.CUSTOM,
        minWidth: "6rem",
      },
      {
        id: "attributes.type",
        label: "dtwins.list.attributes.type",
        width: "8rem",
        formattingProcess: {
          component: "DtwinTypeLabel",
        },
      },
      {
        id: "attributes.serialNumber",
        width: "10rem",
        label: "dtwins.list.attributes.serialNumber",
      },
      {
        id: "attributes.sectorId",
        width: "8rem",
        label: "dtwins.list.attributes.sector",
        formattingProcess: {
          component: "DtwinSector",
        },
      },
      {
        id: "connectivityStatus",
        width: "6rem",
        noVerticalPadding: true,
        label: "dtwins.list.attributes.connectivity.name",
        formattingProcess: {
          component: "DtwinListConnectivityStatus",
        },
      },
      {
        id: "lifeCycleState",
        width: "14rem",
        label: "dtwins.list.attributes.state",
        noVerticalPadding: true,
        formattingProcess: {
          component: "DtwinActionUpdateLifeCycleState",
        },
      },
    ],
  },
  filters: [
    {
      id: "connectivity",
      filter: Filters.DTWIN_CONNECTIVITY_STATUS,
      component: "DtwinFilterConnectivity",
      label: "dtwins.filters.connectivity.label",
    },
    {
      id: "certificateRange",
      filter: Filters.DTWIN_CERTIFICATE_RANGE,
      component: "DtwinFilterCertificateRange",
      label: "dtwins.filters.certificateRange.label",
    },
  ],
  details: {
    description: [
      {
        id: "attributes.friendlyName",
        blockId: "idCard",
        isAllowedToEdit: true,
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.description.idCard.attributes.name",
        formattingProcess: {
          component: "DtwinFriendlyName",
        },
      },
      {
        id: "attributes.type",
        blockId: "idCard",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.description.idCard.attributes.type",
        formattingProcess: {
          component: "DtwinTypeLabel",
        },
      },
      {
        id: "attributes.serialNumber",
        blockId: "idCard",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.description.idCard.attributes.serialNumber",
      },
      {
        id: "attributes.hardwareVersion",
        blockId: "idCard",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.description.idCard.attributes.hardwareVersion",
      },
      {
        id: "attributes.sectorId",
        blockId: "idCard",
        column: DynamicUIDescriptionColumn.RIGHT,
        label: "dtwins.form.description.idCard.attributes.sector",
        formattingProcess: {
          component: "DtwinSector",
        },
      },
      {
        id: "lifeCycleState",
        blockId: "state",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.list.attributes.state",
        formattingProcess: {
          options: [
            {
              enumValue: "preprovisioned",
              label: "dtwins.lifeCycleState.preprovisioned",
            },
            {
              enumValue: "registered",
              label: "dtwins.lifeCycleState.registered",
            },
            {
              enumValue: "activated",
              label: "dtwins.lifeCycleState.activated",
            },
            {
              enumValue: "disabled",
              label: "dtwins.lifeCycleState.disabled",
            },
            {
              enumValue: "lockedPreprovisioned",
              label: "dtwins.lifeCycleState.locked",
            },
            {
              enumValue: "lockedRegistered",
              label: "dtwins.lifeCycleState.locked",
            },
          ],
        },
      },
      {
        id: "firstConnectionDate",
        blockId: "state",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.description.state.attributes.firstConnection",
        formattingProcess: {
          component: "DtwinIsoDate",
        },
      },
      {
        id: "lastConnectionDate",
        blockId: "state",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.description.state.attributes.lastConnection",
        formattingProcess: {
          component: "DtwinIsoDate",
        },
      },
      {
        id: "lastChange",
        blockId: "state",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.description.state.attributes.lastChange",
        formattingProcess: {
          component: "DtwinIsoDate",
        },
      },
      {
        id: "features.configuration.reported.modificationTimestamp",
        blockId: "state",
        column: DynamicUIDescriptionColumn.LEFT,
        label:
          "dtwins.form.description.state.attributes.configurationApplicationDate",
        formattingProcess: {
          component: "DtwinIsoDate",
        },
      },
      {
        id: "features.configuration.reported.firstCommissioningDate",
        blockId: "state",
        column: DynamicUIDescriptionColumn.RIGHT,
        label:
          "dtwins.form.description.state.attributes.firstCommissioningDate",
        formattingProcess: {
          component: "DtwinIsoDate",
        },
      },
      {
        id: "features.certificate.reported.notAfter",
        blockId: "state",
        column: DynamicUIDescriptionColumn.RIGHT,
        label:
          "dtwins.form.description.state.attributes.certificateExpirationDate",
        formattingProcess: {
          component: "DtwinIsoDate",
        },
      },
      {
        id: "features.certificate.reported.$metadata.notAfter.$lastValueUpdate",
        blockId: "state",
        column: DynamicUIDescriptionColumn.RIGHT,
        label: "dtwins.form.description.state.attributes.lastCertificateUpdate",
        formattingProcess: {
          component: "DtwinIsoDate",
        },
      },
      {
        id: "features.firmware.reported.firmwareVersion",
        blockId: "state",
        column: DynamicUIDescriptionColumn.RIGHT,
        label: "dtwins.form.description.state.attributes.firmwareVersion",
      },
    ],
    firmware: [
      {
        id: "features.firmware.reported.firmwareVersion",
        blockId: "information",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.description.state.attributes.firmwareVersion",
      },
      {
        id: "features.firmware.reported.$metadata.firmwareVersion.$lastValueUpdate",
        blockId: "information",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.description.state.attributes.latestFirmwareUpdate",
        formattingProcess: {
          component: "DtwinIsoDate",
        },
      },
      {
        id: "features.firmware.reported.firmwareUpdateSource",
        blockId: "information",
        column: DynamicUIDescriptionColumn.RIGHT,
        label:
          "dtwins.form.firmware.information.attributes.firmwareUpdateSource",
        formattingProcess: {
          options: [
            {
              enumValue: FirmwareUpdateSourceOption.FACTORY,
              label:
                "dtwins.form.firmware.information.attributes.firmwareUpdateSourceOption.factory",
            },
            {
              enumValue: FirmwareUpdateSourceOption.APP,
              labelCustom: APP_NAME,
            },
            {
              enumValue: FirmwareUpdateSourceOption.MOBILE_APP,
              label:
                "dtwins.form.firmware.information.attributes.firmwareUpdateSourceOption.mobileApp",
            },
          ],
        },
      },
    ],
    certificate: [
      {
        id: "features.certificate.reported.notAfter",
        blockId: "information",
        column: DynamicUIDescriptionColumn.LEFT,
        label:
          "dtwins.form.certificate.information.attributes.certificateExpirationDate",
        formattingProcess: {
          component: "DtwinIsoDate",
        },
      },
      {
        id: "features.certificate.reported.origin",
        blockId: "information",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.certificate.information.attributes.origin",
        formattingProcess: {
          options: [
            {
              enumValue: "lxconnect",
              label:
                "dtwins.form.certificate.information.attributes.origins.lxConnect",
            },
            {
              enumValue: "mobile",
              label:
                "dtwins.form.certificate.information.attributes.origins.mobileApplication",
            },
          ],
        },
      },
      {
        id: "features.certificate.reported.$metadata.notAfter.$lastValueUpdate",
        blockId: "information",
        column: DynamicUIDescriptionColumn.RIGHT,
        label:
          "dtwins.form.certificate.information.attributes.lastCertificateUpdate",
        formattingProcess: {
          component: "DtwinIsoDate",
        },
      },
    ],
    configuration: [
      {
        id: "attributes.configurationType",
        blockId: "information",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.configuration.information.configurationType.label",
        formattingProcess: {
          options: [
            {
              enumValue: DtwinConfigurationType.SELF_SUPPORTED,
              label:
                "dtwins.form.configuration.information.configurationType.dtwin.SELF-SUPPORTED",
            },
            {
              enumValue: DtwinConfigurationType.GLOBAL_AND_SPECIFIC,
              label:
                "dtwins.form.configuration.information.configurationType.dtwin.GLOBAL-SPECIFIC",
            },
          ],
        },
      },
      {
        id: "features.configuration.reported.selfSupportingVersion",
        blockId: "information",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.configuration.information.selfSupportingVersion",
      },
      {
        id: "features.configuration.reported.globalVersion",
        blockId: "information",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.configuration.information.globalVersion",
      },
      {
        id: "features.configuration.reported.specificVersion",
        blockId: "information",
        column: DynamicUIDescriptionColumn.LEFT,
        label: "dtwins.form.configuration.information.specificVersion",
      },
      {
        id: "features.configuration.reported.configurationOrigin",
        blockId: "information",
        column: DynamicUIDescriptionColumn.CENTER,
        label: "dtwins.form.configuration.information.configurationOrigin",
      },
      {
        id: "features.configuration.reported.lastConfigurationUpdate",
        blockId: "information",
        column: DynamicUIDescriptionColumn.CENTER,
        label: "dtwins.form.configuration.information.lastUpdate",
        formattingProcess: {
          component: "DtwinIsoDate",
        },
      },
    ],
  },
};
